import React, { useState, useEffect, useRef } from 'react';
import LocationList from '../LocationList';
import '../styles/modes/autoMode.css';

// Import sub-components
import ModeHelpTooltip from './ModeHelpTooltip';
import AlgorithmDetails from './AlgorithmDetails';
import SolveResult from './SolveResult';
import SolverActions from './SolverActions';
import ErrorDisplay from './ErrorDisplay';

// Import custom hooks
import { useAlgorithms } from './hooks/useAlgorithms';
import { useSolveVrp } from './hooks/useSolveVrp';
import { useCapacity } from './hooks/useCapacity';

// Import utilities
import { getAlgorithmOptions } from './utils';

// Import from service module
import { isAutoMode, calculatePathDistance } from '../../Services';

export default function AutoMode({ 
  locations, 
  startId, 
  setLocations, 
  setStartId, 
  setPath, 
  path,
  currentModeRef,
  maxCapacity: externalMaxCapacity,     // Renamed to avoid conflict
  onMaxCapacityChange                   // Add handler prop
}) {
  // Used to record previous locations and startId for change detection
  const prevLocationsRef = useRef(null);
  const prevStartIdRef = useRef(null);
  
  // Help tooltip state
  const [showModeHelp, setShowModeHelp] = useState(false);
  
  // Use custom hooks
  const { 
    algorithms, 
    isLoadingAlgorithms, 
    selectedAlgorithm, 
    handleAlgorithmChange 
  } = useAlgorithms();
  
  const { 
    isSolving, 
    solveError, 
    solveResult, 
    setSolveResult, 
    solvePath: handleSolvePath 
  } = useSolveVrp(setPath);
  
  const { 
    actualMaxCapacity, 
    handleMaxCapacityChange 
  } = useCapacity(externalMaxCapacity, onMaxCapacityChange);
  
  // Get currently selected algorithm information
  const algorithmInfo = algorithms[selectedAlgorithm] || algorithms['nearest_neighbor'];
  
  // Toggle help visibility
  const toggleModeHelp = () => setShowModeHelp(!showModeHelp);
  
  // Monitor changes in locations and startId, reset path if changes occur
  useEffect(() => {
    // Ensure we have previous values for comparison
    if (prevLocationsRef.current !== null && prevStartIdRef.current !== null) {
      // Check if locations have changed (length or content)
      const locationsChanged = 
        prevLocationsRef.current.length !== locations.length ||
        JSON.stringify(prevLocationsRef.current) !== JSON.stringify(locations);
      
      // Check if startId has changed
      const startIdChanged = prevStartIdRef.current !== startId;
      
      // If changes occur and current path exists, reset path
      if ((locationsChanged || startIdChanged) && path && path.length > 0) {

        // Ensure we only clear path in auto mode
        if (currentModeRef && isAutoMode(currentModeRef.current)) {
          setPath([]);
          setSolveResult(null);
        }
      }
    }
    
    // Update reference values for next comparison
    prevLocationsRef.current = [...locations];
    prevStartIdRef.current = startId;
  }, [locations, startId, path, setPath, currentModeRef, setSolveResult]);
  
  // Update solveResult when path or locations change
  useEffect(() => {
    if (path && path.length > 1 && locations && locations.length > 0) {
      const distance = calculatePathDistance(path, locations);
      // Estimate time (assuming average speed of 40km/h)
      const avgSpeed = 40; // km/h
      const time = distance / avgSpeed * 60; // Convert to minutes
      
      // Get vehicle path data (if exists)
      const vehiclePaths = path.vehiclePaths || [];
      
      setSolveResult({
        path: path,
        distance: distance,
        time: time,
        algorithm: selectedAlgorithm,
        vehiclePaths: vehiclePaths,
        maxCapacity: actualMaxCapacity,
        vehicleCount: vehiclePaths.length || 0 // Use vehicle count returned from API
      });
    } else {
      setSolveResult(null);
    }
  }, [path, locations, selectedAlgorithm, actualMaxCapacity, setSolveResult]);
  
  // Wrapper function for solve path
  const solvePath = () => {
    handleSolvePath(locations, startId, selectedAlgorithm, actualMaxCapacity);
  };
  
  return (
    <div className="auto-mode-container">
      <div className="mode-description">
        <div className="mode-title-container">
          <h3>Auto Find Path</h3>
          <button 
            className="mode-help-button"
            onClick={toggleModeHelp}
            title="Help for Auto Path mode"
          >
            ?
          </button>
        </div>
        <p>Use algorithms to automatically find optimal or near-optimal paths.</p>
      </div>
      
      {/* Mode help tooltip */}
      {showModeHelp && <ModeHelpTooltip onClose={() => setShowModeHelp(false)} />}
      
      {/* Display location list, allow editing type and start point, but not deletion */}
      <LocationList 
        locations={locations}
        startId={startId}
        mode="Auto Find Path"
        readOnly={false}
        showActions={true}
        allowDelete={false}
        allowSetStart={true}
        onLocationChange={setLocations}
        onStartIdChange={setStartId}
        maxCapacity={actualMaxCapacity}
        onMaxCapacityChange={handleMaxCapacityChange}
        showMaxCapacity={true}
      />
      
      {locations.length > 0 ? (
        <div className="auto-path-solver">
          {/* Display solve result - moved to top */}
          <SolveResult 
            solveResult={solveResult} 
            algorithms={algorithms} 
            locations={locations} 
          />
          
          {/* Solve button and algorithm selector */}
          <SolverActions 
            locations={locations}
            startId={startId}
            isSolving={isSolving}
            isLoadingAlgorithms={isLoadingAlgorithms}
            selectedAlgorithm={selectedAlgorithm}
            handleAlgorithmChange={handleAlgorithmChange}
            solvePath={solvePath}
            getAlgorithmOptions={() => getAlgorithmOptions(algorithms)}
          />
          
          {/* Display error message */}
          <ErrorDisplay solveError={solveError} />
          
          {/* Algorithm details panel - moved to bottom */}

          <AlgorithmDetails 
            algorithmInfo={algorithmInfo} 
            isLoadingAlgorithms={isLoadingAlgorithms} 
          />
        </div>
      ) : null}
    </div>
  );
} 